import { useMemo } from 'react';
import { useStashScopedStore } from './context';
import {
  buildViewModels,
  applyFilters,
  buildCategoryTree,
  computeMostRecentlyContacted,
  computeMostFrequentlyContacted,
  toStashCardPresentation,
} from './pipeline';
import { buildCategoryDetail, type CategoryDetailVM } from './categorySelectors';
import type { CategoryViewModel, StashCardPresentation, StashCardViewModel } from './types';

export function useStashCardViewModels(): StashCardViewModel[] {
  const scopedStashCards = useStashScopedStore(s => s.scopedStashCards);
  const stashers = useStashScopedStore(s => s.stashers);
  const vendors = useStashScopedStore(s => s.vendors);
  const sortKey = useStashScopedStore(s => s.sortKey);
  const scope = useStashScopedStore(s => s.scope);
  const currentUserStasherId = useStashScopedStore(s => s.currentUserStasherId);

  return useMemo(
    () => buildViewModels(scopedStashCards, stashers, vendors, sortKey, currentUserStasherId, scope),
    [scopedStashCards, stashers, vendors, sortKey, currentUserStasherId, scope],
  );
}

export function useFilteredStashCardViewModels(): StashCardViewModel[] {
  const viewModels = useStashCardViewModels();
  const nearMeFilter = useStashScopedStore(s => s.nearMeFilter);
  const scope = useStashScopedStore(s => s.scope);
  const selectedCategoryId = useStashScopedStore(s => s.selectedCategoryId);
  const categories = useStashScopedStore(s => s.categories);

  return useMemo(
    () => applyFilters(viewModels, nearMeFilter, scope, selectedCategoryId, categories),
    [viewModels, nearMeFilter, scope, selectedCategoryId, categories],
  );
}

export function useTopLevelCategoryViewModels(): CategoryViewModel[] {
  const filteredViewModels = useFilteredStashCardViewModels();
  const categories = useStashScopedStore(s => s.categories);
  const scope = useStashScopedStore(s => s.scope);

  return useMemo(
    () => buildCategoryTree(filteredViewModels, categories, scope),
    [filteredViewModels, categories, scope],
  );
}

export function useMostRecentlyContactedVendorIds(): string[] {
  const scopedStashCards = useStashScopedStore(s => s.scopedStashCards);

  return useMemo(
    () => computeMostRecentlyContacted(scopedStashCards),
    [scopedStashCards],
  );
}

export function useMostFrequentlyContactedVendorIds(): string[] {
  const scopedStashCards = useStashScopedStore(s => s.scopedStashCards);

  return useMemo(
    () => computeMostFrequentlyContacted(scopedStashCards),
    [scopedStashCards],
  );
}

// §11.4 — per-stasher variant: pre-filter scoped cards to one stasher before aggregating
export function useMostRecentlyContactedByStasher(stasherId: string): string[] {
  const scopedStashCards = useStashScopedStore(s => s.scopedStashCards);

  return useMemo(
    () => computeMostRecentlyContacted(scopedStashCards.filter(c => c.stasherId === stasherId)),
    [scopedStashCards, stasherId],
  );
}

export function useSelectedCategory() {
  const categories = useStashScopedStore(s => s.categories);
  const selectedCategoryId = useStashScopedStore(s => s.selectedCategoryId);

  return useMemo(
    () => selectedCategoryId === null
      ? null
      : (categories.find(c => c.recordID === selectedCategoryId) ?? null),
    [categories, selectedCategoryId],
  );
}

export function useSubCategoriesOfSelected() {
  const categories = useStashScopedStore(s => s.categories);
  const selectedCategoryId = useStashScopedStore(s => s.selectedCategoryId);

  return useMemo(
    () => selectedCategoryId === null
      ? []
      : categories.filter(c => c.parentCategoryID === selectedCategoryId),
    [categories, selectedCategoryId],
  );
}

export function useStashCardPresentations(): StashCardPresentation[] {
  const filteredViewModels = useFilteredStashCardViewModels();
  const categories = useStashScopedStore(s => s.categories);
  const userLocation = useStashScopedStore(s => s.nearMeFilter.userLocation);

  return useMemo(
    () =>
      filteredViewModels.map(vm =>
        toStashCardPresentation(vm, categories, userLocation ?? undefined),
      ),
    [filteredViewModels, categories, userLocation],
  );
}

/**
 * Build the Category Detail view-model for the given `categoryId`.
 *
 * Reuses the store's `scopedStashCards` slot, which is already pre-filtered
 * by the active scope (and HomeBase) via the store's `scopeCards` step —
 * no duplication of scope semantics here.
 *
 * `friendIds` is stubbed empty for now; wire it once a friends slice exists.
 */
export function useCategoryDetail(categoryId: string): CategoryDetailVM | null {
  const categories = useStashScopedStore(s => s.categories);
  const cardsInScope = useStashScopedStore(s => s.scopedStashCards);
  const currentUserStasherId = useStashScopedStore(s => s.currentUserStasherId);
  const homeBaseName = useStashScopedStore(s => s.homeBase?.name ?? '');
  const friendIds = useMemo(() => new Set<string>(), []);

  return useMemo(
    () =>
      buildCategoryDetail(
        categoryId,
        categories,
        cardsInScope,
        currentUserStasherId,
        friendIds,
        homeBaseName,
      ),
    [categoryId, categories, cardsInScope, currentUserStasherId, friendIds, homeBaseName],
  );
}

/**
 * Presentations for cards whose vendor's category is `leafCategoryId` or any
 * descendant of it, within the active scope. Used by `CategoryDetailPage` in
 * leaf mode to render a `<StashCardList>` for the selected category.
 *
 * Cycle-safe like `buildCategoryDetail` (carries an ancestor `Set` down the
 * walk and short-circuits on re-entry).
 */
export function useCategoryLeafCards(leafCategoryId: string): StashCardPresentation[] {
  const viewModels = useStashCardViewModels();
  const categories = useStashScopedStore(s => s.categories);
  const userLocation = useStashScopedStore(s => s.nearMeFilter.userLocation);

  return useMemo(() => {
    if (!leafCategoryId) return [];

    // Build a children-by-parent map once for the descendant walk.
    const childrenByParent = new Map<string, string[]>();
    for (const c of categories) {
      const pid = c.parentCategoryID ?? '';
      const list = childrenByParent.get(pid);
      if (list) list.push(c.recordID);
      else childrenByParent.set(pid, [c.recordID]);
    }

    const subtreeIds = new Set<string>();
    const walk = (id: string, ancestors: Set<string>): void => {
      if (ancestors.has(id)) return;       // cycle guard
      subtreeIds.add(id);
      const nextAncestors = new Set(ancestors).add(id);
      for (const kid of childrenByParent.get(id) ?? []) walk(kid, nextAncestors);
    };
    walk(leafCategoryId, new Set());

    return viewModels
      .filter(vm => vm.vendor.categories.some(cat => subtreeIds.has(cat.recordID)))
      .map(vm => toStashCardPresentation(vm, categories, userLocation ?? undefined));
  }, [leafCategoryId, viewModels, categories, userLocation]);
}
